import { DEFAULT_NATURE } from './finance';
import { normalizeText } from './importer';
import type { CostNature, ExpenseCategory, ExpenseEntry, PurchaseDocumentType } from './types';

/**
 * Escaneo de tickets y facturas de proveedor.
 *
 * Este fichero NO habla con ningun OCR. Recibe lo que cualquier motor
 * (Textract, Bedrock, lo que venga) ha sabido leer, ya normalizado en
 * `ScannedDocument`, y se ocupa de la parte que de verdad tiene reglas:
 * interpretar importes y fechas escritos de mil maneras, cuadrar base + IVA
 * contra el total, adivinar la partida y detectar que esa factura ya estaba.
 *
 * El principio, el mismo que en la importacion de catalogo: **nada se guarda
 * sin que el usuario lo haya visto**. Aqui se PROPONE un gasto; escribirlo es
 * otra llamada. Un gasto inventado dentro de la cuenta de resultados es peor
 * que no tener el gasto: descuadra el mes y nadie sabe de donde salio.
 */

/* ------------------------------------------------------------------ */
/* Lo que entrega el OCR                                               */
/* ------------------------------------------------------------------ */

/** Un campo leido, con la confianza que le da el motor (0-100). */
export interface ScannedField {
  value: string;
  confidence?: number;
}

export interface ScannedLine {
  description?: string;
  quantity?: string;
  unitPrice?: string;
  total?: string;
}

/** Salida del OCR ya normalizada: sin nada especifico de AWS. */
export interface ScannedDocument {
  pages?: number;
  vendorName?: ScannedField;
  /**
   * A QUIEN va dirigida la factura: el "Facturar a", que es el propio
   * restaurante. No es el proveedor, y por eso viaja aparte: Textract lo
   * devuelve como RECEIVER_NAME y hasta el 1 de septiembre se estaba metiendo
   * en `vendorName`, con lo que el cliente acababa dado de alta como
   * proveedor. Se guarda para poder descartarlo, no para usarlo.
   */
  receiverName?: ScannedField;
  vendorTaxId?: ScannedField;
  invoiceNumber?: ScannedField;
  invoiceDate?: ScannedField;
  dueDate?: ScannedField;
  /** Total con impuestos. */
  total?: ScannedField;
  /** Base imponible. */
  subtotal?: ScannedField;
  /** Cuota de impuesto. */
  tax?: ScannedField;
  /** Tipo impositivo si viene explicito ("21%", "16"). */
  taxRate?: ScannedField;
  lines?: ScannedLine[];
  /** Texto completo, para las reglas de categoria. */
  rawText?: string;
}

/* ------------------------------------------------------------------ */
/* Importes                                                            */
/* ------------------------------------------------------------------ */

/**
 * Interpreta un importe escrito como sea.
 *
 * El caso peliagudo es "1.234" o "1,234": en Espana son mil doscientos
 * treinta y cuatro, y en Mexico o Estados Unidos son uno con doscientos
 * treinta y cuatro. La regla que usamos:
 *
 *  - Si hay coma Y punto, el ultimo que aparece es el decimal.
 *  - Si solo hay uno y le siguen exactamente 2 cifras, es decimal.
 *  - Si solo hay uno y le siguen 3 cifras, es separador de miles.
 *
 * No es infalible ("1,234" con dos sentidos legitimos), pero es la lectura
 * que acierta en la inmensa mayoria de facturas reales, y el usuario ve el
 * importe antes de guardar.
 */
export function parseImporte(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  // Fuera moneda, espacios raros y el signo de negativo entre parentesis.
  const negativo = /^\(.*\)$/.test(raw.trim());
  let s = raw
    .replace(/[  ]/g, ' ')
    .replace(/[()]/g, '')
    .replace(/[^\d.,-]/g, '')
    .trim();
  if (s === '' || s === '-') return undefined;

  const ultimaComa = s.lastIndexOf(',');
  const ultimoPunto = s.lastIndexOf('.');

  if (ultimaComa >= 0 && ultimoPunto >= 0) {
    const decimal = ultimaComa > ultimoPunto ? ',' : '.';
    const miles = decimal === ',' ? '.' : ',';
    s = s.split(miles).join('').replace(decimal, '.');
  } else if (ultimaComa >= 0 || ultimoPunto >= 0) {
    const sep = ultimaComa >= 0 ? ',' : '.';
    const decimales = s.length - s.lastIndexOf(sep) - 1;
    if (decimales === 3) {
      // Tres cifras detras y nada mas: separador de miles.
      s = s.split(sep).join('');
    } else {
      s = s.split(sep).join('#').replace(/#(?=[^#]*$)/, '.').split('#').join('');
    }
  }

  const n = Number(s);
  if (!Number.isFinite(n)) return undefined;
  const valor = Math.round(Math.abs(n) * 100) / 100;
  return negativo || n < 0 ? -valor : valor;
}

/* ------------------------------------------------------------------ */
/* Fechas                                                              */
/* ------------------------------------------------------------------ */

const MESES: Record<string, number> = {
  ene: 1, enero: 1, jan: 1, feb: 2, febrero: 2, mar: 3, marzo: 3, abr: 4, abril: 4, apr: 4,
  may: 5, mayo: 5, jun: 6, junio: 6, jul: 7, julio: 7, ago: 8, agosto: 8, aug: 8,
  sep: 9, sept: 9, septiembre: 9, oct: 10, octubre: 10, nov: 11, noviembre: 11,
  dic: 12, diciembre: 12, dec: 12,
};

const valida = (a: number, m: number, d: number): string | undefined => {
  if (m < 1 || m > 12 || d < 1 || d > 31) return undefined;
  if (a < 2000 || a > 2100) return undefined;
  const fecha = new Date(Date.UTC(a, m - 1, d));
  if (fecha.getUTCMonth() !== m - 1 || fecha.getUTCDate() !== d) return undefined;
  return fecha.toISOString().slice(0, 10);
};

/**
 * Interpreta una fecha de factura y la devuelve en ISO.
 *
 * Se asume dia/mes, que es como se escribe en Espana y en Mexico. Cuando el
 * primer numero es mayor que 12 no hay ambiguedad; cuando los dos podrian ser
 * mes, mandamos dia primero a proposito y el usuario lo ve antes de guardar.
 */
export function parseFecha(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const s = raw.trim();

  // ISO directo: 2026-08-15
  const iso = s.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (iso) return valida(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  // 15/08/2026, 15-8-26, 15.08.2026
  const num = s.match(/(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
  if (num) {
    let anyo = Number(num[3]);
    if (anyo < 100) anyo += 2000;
    const uno = Number(num[1]);
    const dos = Number(num[2]);
    // Si el primero no puede ser mes, es dia; si el segundo no puede serlo,
    // entonces el formato venia al reves.
    if (dos > 12 && uno <= 12) return valida(anyo, uno, dos);
    return valida(anyo, dos, uno);
  }

  // 15 de agosto de 2026 · 15 ago 2026
  const texto = s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .match(/(\d{1,2})\s*(?:de\s+)?([a-z]+)\.?\s*(?:de\s+)?(\d{2,4})/);
  if (texto) {
    const mes = MESES[texto[2]];
    if (mes) {
      let anyo = Number(texto[3]);
      if (anyo < 100) anyo += 2000;
      return valida(anyo, mes, Number(texto[1]));
    }
  }
  return undefined;
}

/* ------------------------------------------------------------------ */
/* IVA                                                                 */
/* ------------------------------------------------------------------ */

/** Tipos que existen de verdad, por pais. Se usa para redondear el derivado. */
export const TIPOS_IVA: Record<string, number[]> = {
  ES: [0, 4, 10, 21],
  MX: [0, 8, 16],
  CO: [0, 5, 19],
  AR: [0, 10.5, 21],
  CL: [0, 19],
  PE: [0, 18],
};

const tiposDe = (pais: string): number[] => TIPOS_IVA[pais?.toUpperCase()] ?? TIPOS_IVA.ES;

/**
 * Busca el tipo impositivo impreso en el texto del documento ("IVA 10%",
 * "I.V.A. 21 %", "VAT 16%"). Textract no siempre lo devuelve como campo,
 * pero en los tickets espanyoles casi siempre esta escrito — y usarlo evita
 * desglosar el total con un tipo por defecto equivocado, que fue lo que le
 * paso a Pol con su primer ticket real (19 ago).
 */
export function tipoEnTexto(rawText: string | undefined): number | undefined {
  if (!rawText) return undefined;
  const m = rawText
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .match(/(?:^|[^a-z])(?:i\.?v\.?a\.?|vat|impuesto)\s*:?\s*\(?\s*(\d{1,2}(?:[.,]\d{1,2})?)\s*%/i);
  if (!m) return undefined;
  const n = Number(m[1].replace(',', '.'));
  return Number.isFinite(n) && n > 0 && n < 100 ? n : undefined;
}

/**
 * Numero de factura buscado en el texto, para cuando Textract no lo etiqueta.
 *
 * En la factura con la que Alex probo el escaner, "LE-2312" salia en un recuadro
 * bajo la palabra "Numero" y Textract no lo devolvia como INVOICE_RECEIPT_ID:
 * el gasto entraba sin numero, que es justo el dato con el que se detectan las
 * facturas duplicadas.
 *
 * Deliberadamente estricto: exige la palabra delante (factura, numero, n.º...)
 * y descarta lo que parece una fecha o un NIF. Antes que colar un numero
 * inventado, se prefiere dejarlo vacio y que lo escriba el usuario.
 */
export function numeroFacturaEnTexto(rawText: string | undefined): string | undefined {
  if (!rawText) return undefined;
  const limpio = rawText.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  /*
   * La palabra delante, y el candidato OBLIGADO a llevar un digito (el
   * lookahead). Sin el, "FACTURA Numero: LE-2312" se comia "Numero" como
   * candidato, lo descartabamos por no tener cifras y la busqueda seguia
   * DESPUES de la palabra clave, sin llegar nunca al numero de verdad.
   */
  const re =
    /(?:^|[^a-z])(?:factura|numero|num|nro|invoice|ref)\s*[.:nº°]*\s*((?=[A-Z0-9/-]*\d)[A-Z0-9][A-Z0-9/-]{2,19})/gi;
  for (const m of limpio.matchAll(re)) {
    const candidato = m[1].trim().replace(/[.,;]$/, '');
    // Una fecha (28/09/2026) o un numero suelto y largo no es una referencia.
    if (/^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(candidato)) continue;
    if (/^\d{8,}$/.test(candidato)) continue;
    // Tiene que llevar al menos un digito: "FACTURA SIMPLIFICADA" no vale.
    if (!/\d/.test(candidato)) continue;
    return candidato.toUpperCase();
  }
  return undefined;
}

/**
 * Ajusta un tipo calculado al tipo real mas cercano, pero solo si esta muy
 * cerca. Un 20,7 % es un 21 % con redondeos de por medio; un 15 % no es
 * ninguno de los espanoles y hay que dejarlo tal cual para que se vea raro.
 */
export function ajustarTipoIva(calculado: number, pais: string): number {
  const candidatos = tiposDe(pais);
  const cerca = candidatos.find((t) => Math.abs(t - calculado) <= 0.6);
  return cerca ?? Math.round(calculado * 100) / 100;
}

/* ------------------------------------------------------------------ */
/* Categoria                                                           */
/* ------------------------------------------------------------------ */

/**
 * Pistas por palabra clave. Deliberadamente cortas y en minusculas sin
 * acentos: se comparan contra el texto normalizado del documento.
 *
 * No pretende acertar siempre. Pretende que el caso repetido —la factura de
 * la luz, la del gestor, la del proveedor de carne— venga ya con la partida
 * puesta, que es donde esta el ahorro de tiempo.
 */
const PISTAS: Array<{ categoria: ExpenseCategory; palabras: string[] }> = [
  { categoria: 'suministros_electricidad', palabras: ['endesa', 'iberdrola', 'naturgy luz', 'electricidad', 'energia', 'luz', 'cfe', 'edp'] },
  { categoria: 'suministros_agua', palabras: ['aguas', 'agbar', 'canal de isabel', 'agua potable', 'sacmex'] },
  { categoria: 'suministros_gas', palabras: ['gas natural', 'butano', 'propano', 'repsol butano', 'gas lp'] },
  { categoria: 'telefonia', palabras: ['movistar', 'vodafone', 'orange', 'jazztel', 'telcel', 'telefonica', 'yoigo', 'masmovil'] },
  { categoria: 'seguros', palabras: ['seguros', 'mapfre', 'axa', 'allianz', 'zurich', 'generali', 'poliza'] },
  { categoria: 'alquiler', palabras: ['alquiler', 'arrendamiento', 'renta del local', 'arrendador'] },
  { categoria: 'honorarios', palabras: ['gestoria', 'asesoria', 'abogados', 'notaria', 'consultoria', 'contador', 'auditoria'] },
  { categoria: 'comisiones_bancarias', palabras: ['comision', 'banco', 'tpv', 'redsys', 'stripe', 'datafono'] },
  { categoria: 'publicidad_rrss', palabras: ['publicidad', 'marketing', 'meta platforms', 'google ads', 'instagram ads'] },
  { categoria: 'comunicacion_web', palabras: ['hosting', 'dominio', 'aws', 'software', 'suscripcion', 'licencia'] },
  { categoria: 'uniformidad', palabras: ['uniforme', 'vestuario', 'ropa de trabajo', 'calzado laboral'] },
  { categoria: 'papeleria', palabras: ['papeleria', 'oficina', 'imprenta', 'material de oficina'] },
  { categoria: 'reparaciones', palabras: ['reparacion', 'mantenimiento', 'fontaneria', 'electricista', 'averia', 'sat'] },
  { categoria: 'otros_insumos', palabras: ['limpieza', 'higiene', 'desechable', 'servilleta', 'envase', 'menaje'] },
  { categoria: 'compras_materia_prima', palabras: ['carniceria', 'pescaderia', 'fruteria', 'distribuciones', 'alimentacion', 'bodega', 'vinos', 'panaderia', 'lacteos', 'mercamadrid', 'mercabarna', 'congelados'] },
];

/**
 * ¿Aparece esta palabra (o esta frase) como palabra suelta en el texto?
 *
 * Con `includes` a secas, una factura de pescado se clasificaba como
 * electricidad: "merluza" contiene "luz". Los limites de palabra son
 * imprescindibles aqui, y por eso las frases de varias palabras siguen
 * funcionando ("gas natural" se busca entera).
 */
function contienePalabra(texto: string, palabra: string): boolean {
  const escapada = palabra.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?:^|\\s)${escapada}(?:\\s|$)`).test(texto);
}

/**
 * Adivina la partida. El orden importa: primero lo que ya hizo el usuario con
 * ese proveedor, porque su criterio manda sobre cualquier lista nuestra.
 */
export function adivinarCategoria(
  doc: ScannedDocument,
  historico?: Map<string, ExpenseCategory>,
): { categoria: ExpenseCategory; segura: boolean } {
  const proveedor = normalizeText(doc.vendorName?.value ?? '');
  if (proveedor && historico?.has(proveedor)) {
    return { categoria: historico.get(proveedor)!, segura: true };
  }

  const texto = normalizeText(`${doc.vendorName?.value ?? ''} ${doc.rawText ?? ''}`);
  for (const pista of PISTAS) {
    if (pista.palabras.some((p) => contienePalabra(texto, p))) {
      return { categoria: pista.categoria, segura: false };
    }
  }
  // Sin pistas, en un restaurante lo mas probable es materia prima. Se marca
  // como insegura para que la pantalla lo pida confirmar.
  return { categoria: 'compras_materia_prima', segura: false };
}

/* ------------------------------------------------------------------ */
/* Propuesta de gasto                                                  */
/* ------------------------------------------------------------------ */

export interface SupplierRef {
  supplierId: string;
  name: string;
}

export interface ScanContext {
  /** Proveedores ya dados de alta, para enlazar en vez de duplicar. */
  suppliers?: SupplierRef[];
  /** Partida que el usuario suele usar con cada proveedor (normalizado). */
  categoriaHabitual?: Map<string, ExpenseCategory>;
  /** Gastos recientes, para detectar que esta factura ya estaba. */
  recientes?: GastoComparable[];
  /** Que se esta escaneando: el duplicado se busca entre documentos del mismo tipo. */
  documentType?: PurchaseDocumentType;
  /** Pais del negocio: decide los tipos de IVA plausibles. */
  country?: string;
  /** IVA por defecto de los ajustes, si no se puede deducir del documento. */
  vatPctPorDefecto?: number;
  /** Hoy, en ISO. Inyectable para que los tests no dependan del reloj. */
  today?: string;
}

export type Confianza = 'alta' | 'media' | 'baja';

export interface ExpenseDraft {
  concept: string;
  supplierId?: string;
  /** Nombre leido cuando no hay proveedor dado de alta que le encaje. */
  supplierName?: string;
  invoiceNumber?: string;
  date: string;
  dueDate?: string;
  category: ExpenseCategory;
  nature: CostNature;
  netAmount: number;
  vatPct: number;
  paymentType: 'contado' | 'credito';
  invoiceStatus: 'facturado' | 'pendiente' | 'sin_factura';
  paid: boolean;
  /** Total con impuestos tal y como se leyo, solo para ensenarlo. */
  totalLeido?: number;
  confianza: Record<'proveedor' | 'fecha' | 'importe' | 'categoria', Confianza>;
  /** Lo que no cuadra o no se ha podido leer. En castellano llano. */
  avisos: string[];
  /**
   * Gasto que parece el mismo. Va en su propio campo y NO en `avisos`: la
   * pantalla tiene que poder tratarlo distinto —bloqueando el guardado hasta
   * que se confirme— y no como una linea mas de una lista que se ojea.
   */
  duplicado?: DuplicadoInfo;
}

const conf = (campo: ScannedField | undefined, encontrado: boolean): Confianza => {
  if (!encontrado) return 'baja';
  const c = campo?.confidence;
  if (c == null) return 'media';
  if (c >= 90) return 'alta';
  if (c >= 70) return 'media';
  return 'baja';
};

/**
 * Convierte lo leido en un gasto propuesto. Nunca lanza: un documento que no
 * se ha entendido devuelve un borrador con avisos, porque la pantalla tiene
 * que poder ensenar el resultado igualmente para que se corrija a mano.
 */
export function buildExpenseDraft(doc: ScannedDocument, ctx: ScanContext = {}): ExpenseDraft {
  const avisos: string[] = [];
  const hoy = ctx.today ?? new Date().toISOString().slice(0, 10);
  const pais = ctx.country ?? 'ES';

  /* --- Importes --- */
  const total = parseImporte(doc.total?.value);
  const subtotal = parseImporte(doc.subtotal?.value);
  const cuota = parseImporte(doc.tax?.value);
  const tipoLeido = parseImporte(doc.taxRate?.value) ?? tipoEnTexto(doc.rawText);

  let netAmount: number | undefined;
  let vatPct: number | undefined;

  if (subtotal != null && cuota != null && subtotal > 0) {
    netAmount = subtotal;
    vatPct = ajustarTipoIva((cuota / subtotal) * 100, pais);
    if (total != null && Math.abs(subtotal + cuota - total) > 0.02) {
      avisos.push(
        `La base (${subtotal.toFixed(2)}) más el IVA (${cuota.toFixed(2)}) no suman el total (${total.toFixed(2)}). Revisa los importes.`,
      );
    }
  } else if (subtotal != null && total != null && total > subtotal) {
    netAmount = subtotal;
    vatPct = ajustarTipoIva(((total - subtotal) / subtotal) * 100, pais);
  } else if (total != null) {
    // Solo tenemos el total: se desglosa con el tipo leido o el de los ajustes.
    vatPct = tipoLeido != null && tipoLeido > 0 && tipoLeido < 100
      ? ajustarTipoIva(tipoLeido, pais)
      : (ctx.vatPctPorDefecto ?? tiposDe(pais)[tiposDe(pais).length - 1]);
    netAmount = Math.round((total / (1 + vatPct / 100)) * 100) / 100;
    avisos.push(
      `Solo hemos leído el total (${total.toFixed(2)}). Hemos calculado la base con un IVA del ${vatPct} %: compruébalo.`,
    );
  } else if (subtotal != null) {
    netAmount = subtotal;
    vatPct = ctx.vatPctPorDefecto ?? tiposDe(pais)[tiposDe(pais).length - 1];
    avisos.push('No hemos encontrado el total de la factura.');
  }

  if (netAmount == null || netAmount <= 0) {
    avisos.push('No hemos podido leer el importe. Escríbelo a mano.');
    netAmount = 0;
  }
  if (vatPct == null) vatPct = ctx.vatPctPorDefecto ?? 21;

  /* --- Fechas --- */
  const fechaLeida = parseFecha(doc.invoiceDate?.value);
  if (!fechaLeida) avisos.push('No hemos podido leer la fecha; hemos puesto la de hoy.');
  const date = fechaLeida ?? hoy;
  if (fechaLeida && fechaLeida > hoy) {
    avisos.push('La fecha de la factura es futura. Compruébala.');
  }

  const vencimiento = parseFecha(doc.dueDate?.value);
  const dueDate = vencimiento && vencimiento >= date ? vencimiento : undefined;

  /* --- Proveedor --- */
  /*
   * Nunca el "Facturar a". Aunque el mapeo de Textract ya los separa, si los
   * dos campos traen el mismo texto es que el documento no distingue emisor de
   * receptor: en ese caso preferimos no proponer proveedor a proponer el
   * cliente. Antes que un dato incorrecto con pinta de confirmado, un hueco.
   */
  const receptor = normalizeText(doc.receiverName?.value ?? '');
  const leido = doc.vendorName?.value?.trim();
  const esElCliente = Boolean(leido) && receptor !== '' && normalizeText(leido!) === receptor;
  const confianzaNombre = conf(doc.vendorName, Boolean(leido));
  // Con confianza baja tampoco se propone: se deja vacio y se avisa.
  const nombreLeido = esElCliente || confianzaNombre === 'baja' ? undefined : leido;
  if (esElCliente) {
    avisos.push(
      `«${leido}» aparece en la factura como destinatario, no como emisor: no lo damos por proveedor. Escríbelo tú.`,
    );
  } else if (leido && confianzaNombre === 'baja') {
    avisos.push(
      `Hemos leído «${leido}» como proveedor, pero con poca seguridad. Lo dejamos sin asignar para que lo confirmes.`,
    );
  } else if (!leido) {
    avisos.push('No hemos podido leer el proveedor. Elígelo o créalo a mano.');
  }
  const proveedor = emparejarProveedor(nombreLeido, ctx.suppliers ?? []);

  /* --- Categoria --- */
  const { categoria, segura } = adivinarCategoria(doc, ctx.categoriaHabitual);

  /* --- Duplicado --- */
  const invoiceNumber =
    doc.invoiceNumber?.value?.trim() || numeroFacturaEnTexto(doc.rawText) || undefined;
  if (!invoiceNumber) {
    avisos.push('No hemos encontrado el número de factura. Sin él no podemos avisarte si la repites.');
  }
  const duplicado = buscarDuplicado(
    { supplierId: proveedor?.supplierId, invoiceNumber, date, netAmount },
    ctx.recientes ?? [],
    ctx.documentType ?? 'factura',
  );

  return {
    // El concepto sale del emisor; nunca del "Facturar a", que es uno mismo.
    concept: nombreLeido || 'Gasto escaneado',
    supplierId: proveedor?.supplierId,
    supplierName: proveedor ? undefined : nombreLeido,
    invoiceNumber,
    date,
    dueDate,
    category: categoria,
    nature: DEFAULT_NATURE[categoria] ?? 'variable',
    netAmount,
    vatPct,
    // Con vencimiento es una factura a credito; sin el, se da por pagada.
    paymentType: dueDate ? 'credito' : 'contado',
    invoiceStatus: 'facturado',
    paid: !dueDate,
    totalLeido: total,
    confianza: {
      proveedor: conf(doc.vendorName, Boolean(nombreLeido)),
      // (nombreLeido ya viene vacio si era el cliente o si la lectura era mala)
      fecha: conf(doc.invoiceDate, Boolean(fechaLeida)),
      importe: conf(doc.total ?? doc.subtotal, netAmount > 0),
      categoria: segura ? 'alta' : 'baja',
    },
    avisos,
    duplicado,
  };
}

/**
 * Empareja el nombre leido con un proveedor existente. Exacto primero y
 * luego por contencion, porque el OCR suele traer la razon social entera
 * ("DISTRIBUCIONES DEL NORTE SL") donde el usuario guardo el nombre corto.
 */
export function emparejarProveedor(
  nombre: string | undefined,
  suppliers: SupplierRef[],
): SupplierRef | undefined {
  if (!nombre) return undefined;
  const objetivo = normalizeText(nombre);
  if (!objetivo) return undefined;

  const exacto = suppliers.find((s) => normalizeText(s.name) === objetivo);
  if (exacto) return exacto;

  // Solo nombres de 4 letras o mas: "sa" o "sl" harian match con todo.
  const candidatos = suppliers.filter((s) => {
    const n = normalizeText(s.name);
    return n.length >= 4 && (objetivo.includes(n) || n.includes(objetivo));
  });
  // Con varios candidatos no adivinamos: mejor dejarlo sin enlazar.
  return candidatos.length === 1 ? candidatos[0] : undefined;
}

/**
 * Duplicado encontrado, con lo necesario para que el usuario decida.
 *
 * Devolver solo el id no servia de nada: para saber si esa factura ya estaba
 * hay que VER la otra —fecha, concepto, importe, numero— y compararlas. Un
 * aviso que no ensena con que choca obliga a irse a la lista de gastos a
 * buscarlo a mano, y entonces nadie lo comprueba.
 */
export interface DuplicadoInfo {
  entryId: string;
  /**
   * `segura`: mismo numero de factura del mismo proveedor. Es el mismo
   * documento, salvo que el proveedor repita numeracion.
   * `posible`: cuadran importe y fechas, o el numero pero con otro proveedor.
   * Puede ser legitimo (dos pedidos iguales la misma semana).
   */
  certeza: 'segura' | 'posible';
  motivo: 'mismo_numero' | 'mismo_numero_otro_proveedor' | 'mismo_importe_y_fecha';
  /** El gasto que ya existe, para poder compararlo sin salir de la pantalla. */
  existente: {
    date: string;
    concept: string;
    netAmount: number;
    vatPct: number;
    invoiceNumber?: string;
  };
  /** Frase lista para ensenar. Explica por que se sospecha, no solo que. */
  explicacion: string;
}

type GastoComparable = Pick<
  ExpenseEntry,
  | 'entryId'
  | 'supplierId'
  | 'invoiceNumber'
  | 'date'
  | 'netAmount'
  | 'vatPct'
  | 'concept'
  | 'documentType'
>;

const diasEntre = (a: string, b: string): number =>
  Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000;

const resumen = (e: GastoComparable): DuplicadoInfo['existente'] => ({
  date: e.date,
  concept: e.concept,
  netAmount: e.netAmount,
  vatPct: e.vatPct,
  invoiceNumber: e.invoiceNumber,
});

/**
 * ¿Ya tenemos esta factura?
 *
 * Tres reglas, de mas a menos concluyente:
 *
 *  1. Mismo numero de factura y mismo proveedor: es la misma. Certeza alta.
 *  2. Mismo numero pero otro proveedor: sospechoso, porque dos proveedores
 *     distintos pueden usar la misma numeracion. Solo aviso.
 *  3. Sin numero: mismo proveedor, mismo importe y menos de tres dias. Es el
 *     patron de quien escanea dos veces el mismo ticket. Solo aviso, porque
 *     dos pedidos iguales en la misma semana existen de verdad.
 *
 * Nunca decide por el usuario: devuelve el hallazgo y quien mira la pantalla
 * confirma o descarta.
 *
 * Se compara solo contra documentos del MISMO tipo (9 sep 2026, Alex): una
 * factura frente a facturas, un albaran frente a albaranes. Un albaran del
 * mismo proveedor por el mismo importe no es que estes repitiendo la factura,
 * es justo el albaran que esta factura viene a cerrar, y avisar de
 * «duplicado» ahi empujaba a no subir la factura. Pero escanear dos veces el
 * mismo albaran SI es un duplicado, y la primera version de esta regla lo
 * dejo de detectar (revision del 9 sep 2026): por eso el tipo lo dice quien
 * escanea, no se adivina.
 */
export function buscarDuplicado(
  candidato: { supplierId?: string; invoiceNumber?: string; date: string; netAmount: number },
  todos: GastoComparable[],
  tipo: PurchaseDocumentType = 'factura',
): DuplicadoInfo | undefined {
  const recientes = todos.filter((e) => (e.documentType ?? 'factura') === tipo);
  const numero = candidato.invoiceNumber ? normalizeText(candidato.invoiceNumber) : '';

  if (numero) {
    const mismoNumero = recientes.filter(
      (e) => e.invoiceNumber && normalizeText(e.invoiceNumber) === numero,
    );

    const mismoProveedor = mismoNumero.find(
      (e) => !candidato.supplierId || !e.supplierId || e.supplierId === candidato.supplierId,
    );
    if (mismoProveedor) {
      return {
        entryId: mismoProveedor.entryId,
        certeza: 'segura',
        motivo: 'mismo_numero',
        existente: resumen(mismoProveedor),
        explicacion: `Encontramos otra factura del mismo proveedor con el número ${mismoProveedor.invoiceNumber}.`,
      };
    }

    const otroProveedor = mismoNumero[0];
    if (otroProveedor) {
      return {
        entryId: otroProveedor.entryId,
        certeza: 'posible',
        motivo: 'mismo_numero_otro_proveedor',
        existente: resumen(otroProveedor),
        explicacion: `Hay otro gasto con el mismo número de factura (${otroProveedor.invoiceNumber}), aunque de otro proveedor.`,
      };
    }
  }

  if (!candidato.supplierId || candidato.netAmount <= 0) return undefined;

  const parecido = recientes.find(
    (e) =>
      e.supplierId === candidato.supplierId &&
      Math.abs(e.netAmount - candidato.netAmount) < 0.01 &&
      diasEntre(e.date, candidato.date) <= 3,
  );
  if (!parecido) return undefined;

  const dias = Math.round(diasEntre(parecido.date, candidato.date));
  return {
    entryId: parecido.entryId,
    certeza: 'posible',
    motivo: 'mismo_importe_y_fecha',
    existente: resumen(parecido),
    explicacion:
      dias === 0
        ? 'Ya tienes un gasto de este proveedor por el mismo importe y con la misma fecha.'
        : `Ya tienes un gasto de este proveedor por el mismo importe, de hace ${dias} día${dias === 1 ? '' : 's'}.`,
  };
}
